/**
 * Cookie utilities for authentication
 */

// Cookie names based on Supabase's format with project reference
export const AUTH_TOKEN_COOKIE = 'sb-lmwbeeqcqariesjmqfyz-auth-token';
export const REFRESH_TOKEN_COOKIE = 'sb-lmwbeeqcqariesjmqfyz-refresh-token';

/**
 * Get a cookie value by name
 * @param name The cookie name
 * @returns The cookie value or null if not found
 */
export function getCookie(name: string): string | null {
  const cookieStr = document.cookie;
  const cookies = cookieStr.split(';');
  
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    // Check if this cookie starts with the name we want
    if (cookie.indexOf(name + '=') === 0) {
      return decodeURIComponent(cookie.substring(name.length + 1));
    }
  }
  
  return null;
}

/**
 * Get the root domain from the current hostname
 * @returns The root domain (e.g., "float16.cloud" from "app.float16.cloud")
 */
export function getRootDomain(): string {
  // Get from environment variable if available
  const envRootDomain = import.meta.env.VITE_ROOT_DOMAIN;
  if (envRootDomain) {
    return envRootDomain;
  }

  // Extract from hostname as fallback
  const hostParts = window.location.hostname.split('.');
  if (hostParts.length <= 2) {
    // For simple domains like "example.com", return as is
    return window.location.hostname;
  } else {
    // For subdomains like "app.example.com", return "example.com"
    return hostParts.slice(-2).join('.');
  }
}

/**
 * Set a cookie with the given name and value
 * @param name The cookie name
 * @param value The cookie value
 * @param days Number of days until the cookie expires
 * @param secure Whether the cookie should only be transmitted over secure protocol as https
 * @param sameSite SameSite attribute for the cookie
 * @param domain Optional domain for the cookie. If not provided, will use root domain.
 */
export function setCookie(
  name: string,
  value: string,
  days: number = 7,
  secure: boolean = true,
  sameSite: 'strict' | 'lax' | 'none' = 'lax',
  domain?: string
): void {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);

  let cookieValue = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  cookieValue += `; expires=${date.toUTCString()}`;
  cookieValue += `; path=/`;

  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1';

  // Don't set domain on localhost (causes issues)
  if (!isLocalhost) {
    const cookieDomain = domain || getRootDomain();
    cookieValue += `; domain=${cookieDomain}`;
  }

  // Only set secure flag on HTTPS (not localhost HTTP)
  if (secure && window.location.protocol === 'https:') {
    cookieValue += `; secure`;
  }

  // Add SameSite attribute
  cookieValue += `; samesite=${sameSite}`;

  document.cookie = cookieValue;
}

/**
 * Remove a cookie by setting its expiration date to the past
 * @param name The cookie name
 * @param domain Optional domain for the cookie. If not provided, will use root domain.
 */
export function removeCookie(name: string, domain?: string): void {
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1';

  // Pass undefined domain on localhost to avoid setting it
  if (isLocalhost) {
    setCookie(name, '', -1, false, 'lax', undefined);
  } else {
    setCookie(name, '', -1, true, 'lax', domain);
  }
}

/**
 * Get the authentication token from cookies
 * @returns The authentication token or null if not found
 */
export function getAuthToken(): string | null {
  console.log('[getAuthToken] Looking for cookie:', AUTH_TOKEN_COOKIE);
  console.log('[getAuthToken] All cookies:', document.cookie);

  const cookie = getCookie(AUTH_TOKEN_COOKIE);
  console.log('[getAuthToken] Raw cookie value:', cookie ? `${cookie.substring(0, 50)}...` : 'NULL');

  if (!cookie) return null;

  try {
    // Try to parse as JSON in case Supabase stores as JSON string
    const parsed = JSON.parse(cookie);
    if (typeof parsed === 'string') {
      console.log('[getAuthToken] Parsed as JSON string');
      return parsed; // JSON string inside JSON
    } else if (parsed && typeof parsed === 'object') {
      // If it's an object, it might be a session object with token
      console.log('[getAuthToken] Parsed as object, looking for access_token');
      return parsed.access_token || null;
    }
    return null;
  } catch (e) {
    // If not JSON, return as is - likely a direct token string
    console.log('[getAuthToken] Not JSON, returning raw cookie');
    return cookie;
  }
}

/**
 * Set the authentication token in cookies
 * @param token The authentication token
 * @param days Number of days until the cookie expires
 */
export function setAuthToken(token: string, days: number = 7): void {
  // Supabase might store token as stringified JSON or directly as string
  // We'll use the direct string approach as that's compatible with both formats
  setCookie(AUTH_TOKEN_COOKIE, token, days, true, 'lax');
}

/**
 * Remove the authentication token from cookies
 */
export function removeAuthToken(): void {
  removeCookie(AUTH_TOKEN_COOKIE);
}

/**
 * Get the refresh token from cookies
 * @returns The refresh token or null if not found
 */
export function getRefreshToken(): string | null {
  const cookie = getCookie(REFRESH_TOKEN_COOKIE);
  if (!cookie) return null;
  
  try {
    // Try to parse as JSON in case Supabase stores as JSON string
    const parsed = JSON.parse(cookie);
    if (typeof parsed === 'string') {
      return parsed; // JSON string inside JSON
    } else if (parsed && typeof parsed === 'object') {
      // If it's an object, it might be a session object with token
      return parsed.refresh_token || null;
    }
    return null;
  } catch (e) {
    // If not JSON, return as is - likely a direct token string
    return cookie;
  }
}

/**
 * Set the refresh token in cookies
 * @param token The refresh token
 * @param days Number of days until the cookie expires
 */
export function setRefreshToken(token: string, days: number = 30): void {
  // Supabase might store token as stringified JSON or directly as string
  // We'll use the direct string approach as that's compatible with both formats
  setCookie(REFRESH_TOKEN_COOKIE, token, days, true, 'lax');
}

/**
 * Remove the refresh token from cookies
 */
export function removeRefreshToken(): void {
  removeCookie(REFRESH_TOKEN_COOKIE);
}